import { useEffect, useMemo } from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonical: string;
  type?: string;
  keywords?: string;
  schema?: object | object[];
  structuredData?: object | object[];
  ogImage?: {
    url: string;
    width?: number | string;
    height?: number | string;
  };
  lastModified?: string;
  robots?: string; // optional
  ogLocale?: string; // optional
}

export default function SEO({
  title,
  description,
  canonical,
  type = 'website',
  keywords,
  schema,
  structuredData,
  ogImage,
  lastModified,
  robots,
  ogLocale = 'en_US',
}: SEOProps) {
  const resolvedSchema = structuredData ?? schema;

  // IMPORTANT: memoize to avoid infinite/unnecessary effect runs
  const schemas = useMemo(() => {
    if (!resolvedSchema) return [];
    return Array.isArray(resolvedSchema) ? resolvedSchema : [resolvedSchema];
  }, [resolvedSchema]);

  useEffect(() => {
    document.title = title;

    const setMeta = (selector: string, attribute: string, value: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement | null;

      if (!element) {
        element = document.createElement('meta');
        const nameMatch = selector.match(/name="([^"]+)"/);
        const propertyMatch = selector.match(/property="([^"]+)"/);

        if (propertyMatch?.[1]) element.setAttribute('property', propertyMatch[1]);
        if (nameMatch?.[1]) element.setAttribute('name', nameMatch[1]);

        document.head.appendChild(element);
      }

      element.setAttribute(attribute, value);
    };

    // Standard tags
    setMeta('meta[name="description"]', 'content', description);

    if (keywords) setMeta('meta[name="keywords"]', 'content', keywords);
    if (lastModified) setMeta('meta[name="last-modified"]', 'content', lastModified);
    if (robots) setMeta('meta[name="robots"]', 'content', robots);

    // Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical;

    // Open Graph
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', canonical);
    setMeta('meta[property="og:type"]', 'content', type);
    setMeta('meta[property="og:locale"]', 'content', ogLocale);

    if (ogImage?.url) {
      setMeta('meta[property="og:image"]', 'content', ogImage.url);
      if (ogImage.width) setMeta('meta[property="og:image:width"]', 'content', String(ogImage.width));
      if (ogImage.height) setMeta('meta[property="og:image:height"]', 'content', String(ogImage.height));
    }

    // Twitter
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    if (ogImage?.url) setMeta('meta[name="twitter:image"]', 'content', ogImage.url);

    // JSON-LD: remove previous, add ONE script
    document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());

    if (schemas.length) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');

      // If multiple schemas, you can wrap in @graph automatically
      const payload =
        schemas.length === 1
          ? schemas[0]
          : { "@context": "https://schema.org", "@graph": schemas };

      script.textContent = JSON.stringify(payload);
      document.head.appendChild(script);
    }
  }, [title, description, canonical, type, keywords, lastModified, ogImage, robots, ogLocale, schemas]);

  return (
    <div
      id="__seo_data__"
      data-seo-title={title}
      data-seo-description={description}
      data-seo-canonical={canonical}
      data-seo-type={type}
      data-seo-keywords={keywords || ''}
      data-seo-last-modified={lastModified || ''}
      data-seo-og-image={ogImage?.url || ''}
      data-seo-og-image-width={ogImage?.width || ''}
      data-seo-og-image-height={ogImage?.height || ''}
      data-seo-schemas={JSON.stringify(schemas)}
      style={{ display: 'none' }}
      aria-hidden="true"
    />
  );
}